import React, {ReactNode} from "react";
import {IDetailedField} from "../detailed-item/field-descriptions";
import {Button, DatePicker, Form, Input, InputNumber, Select, Space, Switch} from "antd";
import {DISPOSITIONS, DispositionSelect} from "../dispositions/selection";
import {ITFOP} from "../../tevapi/interfaces";
import useFormInstance from "antd/es/form/hooks/useFormInstance";
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import dayjs from "dayjs";


interface ITEVInput {
  value?: any;
  onChange?: (argo0: any) => void;
  status: string | null | undefined;
  errors: ReactNode[] | string | null | undefined;
  field: IDetailedField;
};


export const NumberInput: React.FC<ITEVInput> = (props) => {
  return (
    <InputNumber />
  );
};

export const StrInput: React.FC<ITEVInput> = (props) => {
  const form = useFormInstance();
  const value = Form.useWatch(props.field.field, form);
  return (
    <Input
      value={value}
      onChange={(
        e) => form.setFieldValue(props.field.field, e.target.value)
      }
      className={`numeric-input-${props.status}`}
    />
  );
};

export const UncertainDecimalInput: React.FC<ITEVInput> = (props) => {
  const form = useFormInstance();

  const valueField = [props.field.field, "value"];
  const errorField = [props.field.field, "error"];

  const valueValue = Form.useWatch(valueField, form);
  const errorValue = Form.useWatch(errorField, form);

  return (
    <Space size="small">
      <Input
        value={valueValue}
        onChange={(e) => form.setFieldValue(valueField, e.target.value)}
        className={`value-input-${props.status}`}
      />
      <Input
        value={errorValue}
        onChange={(e) => form.setFieldValue(errorField, e.target.value)}
        className={`error-input-${props.status}`}
      />
    </Space>
  );
};


export const TFOPPriorityInput: React.FC<{value: number | null | undefined, placeholder: string, subgroup: keyof ITFOP, field: IDetailedField}> = (props) => {
  const form = useFormInstance();
  const TFOPOptions = [
    {label: "L1 Critical Candidate", value: 1},
    {label: "L1 Candidate", value: 2},
    {label: "Lower Priority Candidate", value: 3},
    {label: "On Hold", value: 4},
    {label: "No More Observations", value: 5}
  ];
  const fieldName = [props.field.field, props.subgroup];
  const subgroupValue = Form.useWatch(fieldName, form);

  return (
    <Select
      placeholder={props.placeholder}
      optionLabelProp="value"
      options={TFOPOptions}
      dropdownStyle={{minWidth: "200px"}}
      value={subgroupValue}
      onChange={
        (v) => {
          form.setFieldValue(fieldName, v);
        }
      }
    />
  );
};

const TFOPInput: React.FC<ITEVInput> = (props) => {
  return (
    <Space.Compact block size="small">
      <TFOPPriorityInput
        subgroup="master"
        placeholder="Master"
        value={props.value?.master}
        field={props.field}
      />
      <TFOPPriorityInput
        subgroup="sg1a"
        placeholder="SG1a"
        value={props.value?.sg1a}
        field={props.field}
      />
      <TFOPPriorityInput
        subgroup="sg1b"
        placeholder="SG1b"
        value={props.value?.sg1b}
        field={props.field}
      />
      <TFOPPriorityInput
        subgroup="sg2"
        placeholder="SG2"
        value={props.value?.sg2}
        field={props.field}
      />
      <TFOPPriorityInput
        subgroup="sg3"
        placeholder="SG3"
        value={props.value?.sg3}
        field={props.field}
      />
      <TFOPPriorityInput
        subgroup="sg4"
        placeholder="SG4"
        value={props.value?.sg4}
        field={props.field}
      />
      <TFOPPriorityInput
        subgroup="sg5"
        placeholder="SG5"
        value={props.value?.sg5}
        field={props.field}
      />
    </Space.Compact>
  );
};


const StringArrayField: React.FC<ITEVInput> = (props) => {
  return (
    <Form.List name={props.field.field}>
      {(fields, {add, remove}) => (
        <Space direction="vertical" size="small">
          {fields.map((field) => (
            <Space key={field.key} direction="horizontal" align="baseline">
              <Form.Item {...field}>
                <Input />
              </Form.Item>
              <Button danger onClick={() => remove(field.name)}>
                <FontAwesomeIcon icon="fa-solid fa-trash-xmark" />
              </Button>
            </Space>
          ))}
          <Button onClick={() => add()}>
            Add to {props.field.title}
          </Button>
        </Space>
      )}
    </Form.List>
  );
};

const NumberArrayField: React.FC<ITEVInput> = (props) => {
  return (
    <Form.List name={props.field.field}>
      {(fields, {add, remove}) => (
        <Space direction="vertical" size="small">
          {fields.map((field) => (
            <Space key={field.key} direction="horizontal" align="baseline">
              <Form.Item {...field}>
                <InputNumber step="1" />
              </Form.Item>
              <Button danger onClick={() => remove(field.name)}>
                <FontAwesomeIcon icon="fa-solid fa-trash-xmark" />
              </Button>
            </Space>
          ))}
          <Button onClick={() => add()}>
            Add to {props.field.title}
          </Button>
        </Space>
      )}
    </Form.List>
  );
};


export const TEVInputField: React.FC<{value?: any, onChange?: any, field: IDetailedField}> = (props) => {
  const { status, errors } = Form.Item.useStatus();


  switch (props.field.type) {
    case "decimal":
      return <InputNumber {...props} />;
    case "number":
      return <InputNumber {...props} />;
    case "integer":
      return <InputNumber step="1" {...props} />;
    case "uncertain_decimal":
      return <UncertainDecimalInput {...{status, errors}} {...props} />;
    case "string":
      return <Input {...props} />;
    case "boolean":
      return <Switch {...{status, errors}} {...props} />;
    case "disposition":
      return <DispositionSelect options={Object.keys(DISPOSITIONS)} {...{status, errors}} {...props} />;
    case "sectors":
      return <NumberArrayField {...{status, errors}} {...props} />
    case "array":
      return <StringArrayField {...{status, errors}} {...props} />
    case "tfop":
      return <TFOPInput {...{status, errors}} {...props} />
    case "datetime":
      return <DatePicker
        showTime
        onChange={props.onChange}
        value={dayjs(props.value)}
        onOk={console.log}
      />
  }
};
